import { StandardEvents } from '@shopify/events';

/**
 * Bigens PDP, below the fold (T43).
 *
 * <bigens-pdp-explainer>: the "How big, how strong, how soft?" tabs.
 * - WAI-ARIA tabs: click or arrow keys (Left/Right/Home/End) move the selection; the selected tab is the only one in
 *   the tab order. Panels are plain tabpanels toggled with the `hidden` attribute (the first one is visible without JS).
 * - The Size and Softness panels hold one visual per size/softness value of the product. When the shopper picks
 *   another variant in the buy box, the matching one is shown (`data-variant-rows` = "variantId:cm:in:softness|…").
 * No copy in JS: every label is rendered in Liquid.
 */
class BigensPdpExplainer extends HTMLElement {
  /** @type {HTMLElement[]} */
  #tabs = [];

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#onClick);
      tab.addEventListener('keydown', this.#onKeydown);
    }
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    for (const tab of this.#tabs) {
      tab.removeEventListener('click', this.#onClick);
      tab.removeEventListener('keydown', this.#onKeydown);
    }
    document.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.currentTarget);
    this.#select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const index = this.#tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    if (index < 0) return;
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.#tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const tab = this.#tabs[next];
    if (!tab) return;
    this.#select(tab);
    tab.focus();
  };

  /** @param {HTMLElement} selected */
  #select(selected) {
    for (const tab of this.#tabs) {
      const on = tab === selected;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (panel) panel.hidden = !on;
    }
  }

  /** @param {Event & {promise?: Promise<{variant?: {id?: string | number} | null, detail?: {resource?: {id?: string | number} | null}}>}} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;
    event.promise
      .then(({ variant, detail }) => {
        const id = detail?.resource?.id ?? variant?.id;
        if (id == null || !this.isConnected) return;
        this.#showVariant(String(id).replace(/^gid:\/\/shopify\/ProductVariant\//, ''));
      })
      .catch(() => {});
  };

  /** @param {string} variantId */
  #showVariant(variantId) {
    const rows = this.querySelector('[data-variant-rows]')?.getAttribute('data-variant-rows') ?? '';
    const row = rows
      .split('|')
      .map((entry) => entry.split(':'))
      .find(([id]) => id === variantId);
    if (!row) return;
    const [, cm, , softness] = row;
    // toggleAttribute, not .hidden: the size scenes are <svg> elements, which have no `hidden` property.
    for (const node of this.querySelectorAll('[data-scene-cm]')) {
      node.toggleAttribute('hidden', node.getAttribute('data-scene-cm') !== cm);
    }
    for (const node of this.querySelectorAll('[data-softness]')) {
      node.toggleAttribute('hidden', node.getAttribute('data-softness') !== softness);
    }
  }
}

if (!customElements.get('bigens-pdp-explainer')) {
  customElements.define('bigens-pdp-explainer', BigensPdpExplainer);
}
